import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Tag, Send, User, Calendar, Users, AlertCircle, Download } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { StatusBadge } from '@/components/ConfidenceBadge';
import ExportPdfDialog from '@/components/ExportPdfDialog';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ReadOnlyList({ items, marker }) {
  const mk = marker === 'dia' ? 'em-dia' : 'em-dot';
  return (
    <ul className="edl">
      {items.map((item, i) => (
        <li key={i} className="eli">
          <span className={mk} />
          <span className="etx" style={{ cursor: 'default' }}>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function ThreadBubble({ r }) {
  const owner = r.is_owner;
  const initial = (r.sender_name || '?').slice(0, 1).toUpperCase();
  return (
    <div className="cv-msg" data-testid="thread-message">
      <span className={`av ${owner ? 'av-blue' : 'av-cream'}`}>{initial}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="cv-msg-head">
          <span className="cv-msg-name">{r.sender_name}{owner ? ' · owner' : ''}</span>
          <span className="cv-msg-time">
            {new Date(r.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        <div className={`cv-msg-body${owner ? ' owner' : ''}`}>{r.message}</div>
      </div>
    </div>
  );
}

export default function SharedCardInAppPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [responses, setResponses] = useState([]);
  const [completing, setCompleting] = useState({});
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);

  const myName = user?.name || 'You';

  useEffect(() => {
    axios.post(`${API_BASE}/a/${token}/viewed`).catch(() => {});
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/a/${token}`);
        setData(res.data);
        setTasks(res.data.card.tasks || []);
        setResponses(res.data.card.responses || []);
      } catch (err) {
        setError(err?.response?.data?.detail || 'This shared card is no longer available.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const toggleTask = async (taskId, status) => {
    if (!data?.share?.permissions?.can_act) return;
    const newStatus = status === 'done' ? 'not_started' : 'done';
    setCompleting(p => ({ ...p, [taskId]: true }));
    try {
      const res = await axios.put(`${API_BASE}/a/${token}/tasks/${taskId}`, { status: newStatus, completed_by: myName });
      setTasks(prev => prev.map(t => t.id === taskId ? res.data : t));
      if (newStatus === 'done') toast.success('Task marked complete');
    } catch { toast.error('Failed to update task'); }
    finally { setCompleting(p => ({ ...p, [taskId]: false })); }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_BASE}/a/${token}/response`, { message: reply, sender_name: myName });
      setResponses(prev => [...prev, { id: `local-${Date.now()}`, sender_name: myName, message: reply, is_owner: false, timestamp: new Date().toISOString() }]);
      setReply('');
      toast.success('Reply sent');
    } catch { toast.error('Failed to send reply'); }
    finally { setSending(false); }
  };

  if (loading) {
    return <div className="doc"><div className="crumb-skel" /><div className="card sec" style={{ height: 140 }} /></div>;
  }

  if (error) {
    return (
      <div className="doc" data-testid="shared-card-inapp-error">
        <button className="crumb" onClick={() => navigate('/shared')}><ArrowLeft size={16} />Shared with me</button>
        <div className="card sec" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <AlertCircle size={26} style={{ color: '#B4332E', margin: '0 auto 12px' }} />
          <div className="lab" style={{ marginBottom: 6 }}>Card unavailable</div>
          <p style={{ fontSize: 14, color: 'var(--mute)' }}>{error}</p>
        </div>
      </div>
    );
  }

  const { card, share } = data;
  const canAct = share?.permissions?.can_act;
  const _cn = (card.contact_name || '').trim().toLowerCase();
  const contactIsPerson = !!_cn && !['unknown', 'unknown contact', 'null', 'none', 'n/a', 'nobody', 'someone'].includes(_cn);
  const title = card.title || (contactIsPerson ? card.contact_name : 'Shared Action Card');
  const useContactSub = !!card.title && contactIsPerson && card.title !== card.contact_name;
  const createdStr = card.created_at
    ? new Date(card.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '';
  const pending = tasks.filter(t => t.status !== 'done');
  const done = tasks.filter(t => t.status === 'done');

  return (
    <div className="doc" data-testid="shared-card-inapp-page">
      <button className="crumb" onClick={() => navigate('/shared')}><ArrowLeft size={16} />Shared with me</button>

      <div className="hd">
        <div className="hd-main">
          <h1 className="ac-title">{title}</h1>
          {useContactSub && <div className="ac-subhead">with {card.contact_name}</div>}
          <div className="ac-meta">
            {tasks.length > 0 && <StatusBadge status={done.length === tasks.length ? 'done' : 'in_progress'} />}
            <span className="dotline">
              {createdStr}
              {card.source ? <>&nbsp; · &nbsp;<span style={{ textTransform: 'capitalize' }}>{card.source === 'audio' ? 'Recorded audio' : card.source}</span></> : null}
            </span>
          </div>
          {card.tags?.length > 0 && (
            <div className="ac-tags">
              {card.tags.map(t => <span key={t} className="ac-tag-chip" style={{ paddingRight: 10 }}><Tag size={11} />{t}</span>)}
            </div>
          )}
        </div>
        <div className="hd-side">
          <span className="shared-by-pill" data-testid="shared-by-pill">
            <Users size={13} />Shared by {share?.shared_by_name || 'a teammate'}
          </span>
          <button
            data-testid="shared-inapp-export-pdf"
            onClick={() => setPdfOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors hover:bg-secondary"
            style={{ color: '#6994c6', borderColor: 'rgba(105,148,198,0.35)', marginTop: 8 }}
          >
            <Download size={13} />Export PDF
          </button>
        </div>
      </div>

      <ExportPdfDialog open={pdfOpen} onOpenChange={setPdfOpen} card={card} tasks={tasks} />

      <div className="doc-body" data-testid="shared-card-body">
        <div className="doc-main">
          {card.summary_bullets?.length > 0 && (
            <section className="card sec">
              <div className="block">
                <div className="lab">Summary</div>
                <ReadOnlyList items={card.summary_bullets} />
              </div>
              {card.decisions?.length > 0 && (
                <>
                  <div className="hr" />
                  <div className="block">
                    <div className="lab">Decisions</div>
                    <ReadOnlyList items={card.decisions} marker="dia" />
                  </div>
                </>
              )}
            </section>
          )}
          {!card.summary_bullets?.length && card.decisions?.length > 0 && (
            <section className="card sec">
              <div className="block">
                <div className="lab">Decisions</div>
                <ReadOnlyList items={card.decisions} marker="dia" />
              </div>
            </section>
          )}
        </div>

        <div className="doc-rail">
          {tasks.length > 0 && (
            <div className="sec">
              <div className="sec-h">
                <span className="lab">Tasks</span>
                <span className="meta">{done.length} of {tasks.length} done</span>
              </div>
              <section className="card" data-testid="shared-card-tasks">
                <div style={{ padding: '6px 8px' }}>
                  {[...pending, ...done].map(task => {
                    const isDone = task.status === 'done';
                    return (
                      <div key={task.id} className={`tk${isDone ? ' done' : ''}`} data-testid="shared-card-task-row">
                        <div className={`ck${isDone ? ' done' : ''}`} role="button" aria-label="Toggle task"
                          data-testid="shared-card-task-checkbox"
                          style={{ cursor: canAct ? 'pointer' : 'default', opacity: canAct ? 1 : 0.7 }}
                          onClick={() => canAct && !completing[task.id] && toggleTask(task.id, task.status)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                        </div>
                        <div className="tk-body">
                          <div className="tk-title"><span className="tk-edit txt" style={{ cursor: 'default' }}>{task.title}</span></div>
                          <div className="tk-meta">
                            {task.owner && <><span className="mi"><User size={12} />{task.owner}</span><span className="sep">·</span></>}
                            {task.due_date && <span className="mi"><Calendar size={12} />{task.due_date}</span>}
                            {task.priority && <span className={`prio ${task.priority}`} style={{ cursor: 'default' }}>{task.priority === 'high' ? 'High' : task.priority === 'low' ? 'Low' : 'Med'}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>
          )}

          {(canAct || responses.length > 0) && (
            <div className="sec">
              <div className="sec-h"><span className="lab">Conversation</span></div>
              <section className="card" data-testid="shared-card-thread" style={{ padding: 16 }}>
                {responses.length > 0 ? (
                  <div className="cv-list">{responses.map(r => <ThreadBubble key={r.id} r={r} />)}</div>
                ) : (
                  <p style={{ fontSize: 12.5, color: 'var(--faint)', marginBottom: canAct ? 14 : 0 }}>
                    No messages yet.{canAct ? ' Start the conversation below.' : ''}
                  </p>
                )}
                {canAct && (
                  <form onSubmit={sendReply} className="cv-form" style={responses.length > 0 ? { borderTop: '1px solid var(--line2)', paddingTop: 14, marginTop: 14 } : {}}>
                    <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} required
                      placeholder="Add a note, question, or update…" className="cv-textarea"
                      data-testid="shared-card-reply-input" />
                    <button type="submit" disabled={sending || !reply.trim()} className="btn btn-primary"
                      data-testid="shared-card-reply-send">
                      <Send size={14} />{sending ? 'Sending…' : (responses.length > 0 ? 'Reply' : 'Send')}
                    </button>
                  </form>
                )}
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
